<script setup>
import { ref, onMounted, computed, reactive, h, nextTick, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useVueTable
} from '@tanstack/vue-table';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Table from 'dashboard/components/table/tableMod/TableMod.vue';
import SortButton from 'dashboard/components/table/SortButton.vue';
import TruncatedCell from 'dashboard/components/table/tableMod/TruncatedCell.vue';
import BaseCell from 'dashboard/components/table/tableMod/BaseCellMod.vue';
import Pagination from 'dashboard/components/table/tableMod/PaginationList.vue';
import AddAgentButton from '../components/list-agentis/AddAgentButton.vue';
import AlertResponza from 'dashboard/components-next/responza/AlertResponza.vue';
import wootAPI from '@/v3/api/apiClient';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const isLoading = ref(true);
const localAgents = ref([]);

// Estado para o modal de confirmação de exclusão
const showDeleteConfirmation = ref(false);
const agentToDelete = ref(null);

// Estado para o modal de confirmação de duplicação
const showDuplicateConfirmation = ref(false);
const agentToDuplicate = ref(null);

// Estado para o modal de configurações do agente
const showAgentSettingsModal = ref(false);
const selectedAgentForSettings = ref(null);
const agentInboxes = ref([]);
const loadingAgentInboxes = ref(false);
const inboxOptionsForModal = ref([]);

// Configuração de ordenação
const sorting = ref([]);

// Vozes disponíveis
const voiceOptions = {
  'male': 'Masculina',
  'female': 'Feminina',
  'neutral': 'Neutra'
};

// Tipos de fluxo
const flowTypes = {
  1: 'Qualificação',
  2: 'Vendas',
  3: 'Agendamento'
};

// Tipos de personalidade
const personalityTypes = {
  1: 'Profissional',
  2: 'Amigável',
  3: 'Consultivo'
};

// Cache para dados parseados de company_data
const companyDataCache = new Map();

// Estado para armazenar se o Google Calendar está conectado
const isGoogleCalendarConnected = ref(false);

// Estado para controle do dropdown de inboxes no modal
const showInboxDropdownModal = ref(false);
const dropdownPositionModal = ref({ top: 0, left: 0 });

// Função helper para parsear company_data com cache
const getParsedCompanyData = (agent) => {
  if (!agent || !agent.company_data) return null;
  
  const cacheKey = `${agent.id}-${JSON.stringify(agent.company_data)}`;
  if (companyDataCache.has(cacheKey)) {
    return companyDataCache.get(cacheKey);
  }
  
  try {
    const companyData = typeof agent.company_data === 'string'
      ? JSON.parse(agent.company_data)
      : agent.company_data;
    companyDataCache.set(cacheKey, companyData);
    return companyData;
  } catch (error) {
    companyDataCache.set(cacheKey, null);
    return null;
  }
};

// Função para obter cookie
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Função para obter headers de autenticação
const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) {
    console.error('cw_d_session_info cookie não encontrado');
    return {};
  }
  try {
    const sessionInfo = JSON.parse(decodeURIComponent(cookie));
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    };
  } catch (e) {
    console.error('Erro ao analisar cw_d_session_info');
    return {};
  }
};

// Função para obter o status detalhado da agenda
const getScheduleStatus = (agent) => {
  if (!agent || agent.flow !== 3) return { isCalendarConnected: false, hasSchedule: false };
  
  return {
    isCalendarConnected: isGoogleCalendarConnected.value,
    hasSchedule: isGoogleCalendarConnected.value
  };
};

// Função para verificar se um agente tem agenda vinculada (mantida para compatibilidade)
const hasLinkedSchedule = (agent) => {
  return getScheduleStatus(agent).hasSchedule;
};

// Função para verificar se o Google Calendar está conectado
const checkGoogleCalendarConnection = async () => {
  try {
    const authHeaders = getAuthHeaders();
    
    const params = new URLSearchParams({
      start: new Date().toISOString(),
      end: new Date().toISOString()
    });
    
    const response = await fetch(`/api/v1/google_calendar?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      credentials: 'include'
    });
    
    if (response.ok) {
      const data = await response.json();
      isGoogleCalendarConnected.value = true;
    } else {
      isGoogleCalendarConnected.value = false;
    }
  } catch (error) {
    isGoogleCalendarConnected.value = false;
  }
};

// Obtemos os agentes do store
const agents = computed(() => {
  // Sempre usar localAgents se foi inicializado (mesmo que vazio)
  if (localAgents.value !== null && localAgents.value !== undefined) {
    return localAgents.value;
  }
  // Fallback para o store se localAgents não foi inicializado
  const storeAgents = store.getters['agents/getAgents'];
  return storeAgents || [];
});

const uiFlags = computed(() => store.getters['agents/getUIFlags']);

// Funções para formatar dados do agente para exibição - usando cache
const getCompanyRole = (agent) => {
  const companyData = getParsedCompanyData(agent);
  return companyData?.role || '-';
};

const getCompanyName = (agent) => {
  const companyData = getParsedCompanyData(agent);
  return companyData?.name || '-';
};

const getCompanyLocation = (agent) => {
  const companyData = getParsedCompanyData(agent);
  if (companyData?.locations && companyData.locations.length > 0) {
    const location = companyData.locations[0];
    return `${location.city}, ${location.state}`;
  }
  return '-';
};

const getCompanyPhone = (agent) => {
  const companyData = getParsedCompanyData(agent);
  return companyData?.support?.phone || '-';
};

const getCompanyEmail = (agent) => {
  const companyData = getParsedCompanyData(agent);
  return companyData?.support?.email || '-';
};

// Carregar agentes
const fetchAgents = async () => {
  isLoading.value = true;
  try {
    // Chamada direta à API caso haja algum problema com o store
    const authHeaders = getAuthHeaders();
    

    const response = await fetch('/api/v1/agents_ai', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      credentials: 'include' // Para incluir cookies na requisição
    });

    if (!response.ok) {
      throw new Error(`Erro ao buscar agentes: ${response.status}`);
    }

    const data = await response.json();
    console.log('Agentes carregados com sucesso');

    // Armazenar os agentes localmente
    if (data && data.agents && Array.isArray(data.agents)) {
      localAgents.value = data.agents;

      // Também atualizar o store
      store.commit('agents/SET_AGENTS', data.agents);
      
      // Verificar scrollbar após carregar os dados
      nextTick(() => {
        checkScrollbar();
      });
    } else {
    }
  } catch (error) {
    // Tentar carregar usando o store como fallback
    try {
      await store.dispatch('agents/getAll');
      // Se bem sucedido, atualizar a variável local
      const storeAgents = store.getters['agents/getAgents'];
      if (storeAgents && Array.isArray(storeAgents)) {
        localAgents.value = storeAgents;
      }
    } catch (storeError) {
      console.error('Erro ao carregar agentes usando o store');
    }
  } finally {
    isLoading.value = false;
  }
};

// Visualizar um agente
const viewAgent = (agent, isDuplicated = false) => {
  // Navegar para a página de detalhes ao invés de abrir o modal
  router.push({
    name: 'agentis_view',
    params: { 
      accountId: route.params.accountId,
      agentId: agent.id 
    }
  });
};

// Atualiza a lista após edição
const handleAgentUpdated = async () => {
  await fetchAgents();
};

// Iniciar processo de exclusão de agente
const initiateDeleteAgent = (agent, event) => {
  if (event) {
    event.stopPropagation();
  }
  agentToDelete.value = agent;
  showDeleteConfirmation.value = true;
};

// Confirmar exclusão do agente
const confirmDeleteAgent = async () => {
  if (!agentToDelete.value) return;
  
  const agent = agentToDelete.value;
  
  try {
      // Chamada direta à API para excluir
      const authHeaders = getAuthHeaders();

      const response = await fetch(`/api/v1/agents_ai/${agent.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Erro ao excluir agente: ${response.status}`);
      }

      // Atualizar localmente removendo o agente da lista
      localAgents.value = localAgents.value.filter(a => a.id !== agent.id);

      console.log('Agente excluído com sucesso');
      
      // Fechar o modal de confirmação
      showDeleteConfirmation.value = false;
      agentToDelete.value = null;
      
      // Se não há mais agentes, recarregar para mostrar a tela vazia corretamente
      if (localAgents.value.length === 0) {
        await fetchAgents();
      }
    } catch (error) {
      console.error('Erro ao excluir agente');
      alert(`Erro ao excluir o agente: ${error.message}`);
      showDeleteConfirmation.value = false;
      agentToDelete.value = null;
    }
};

// Função para transformar o formato antigo do company_data para o novo
const transformCompanyData = (companyData) => {
  // Se não há company_data, retornar vazio
  if (!companyData) return {};
  
  // Parse se for string
  let data = companyData;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (e) {
      return {};
    }
  }
  
  // Clonar o objeto para não modificar o original
  const transformed = JSON.parse(JSON.stringify(data));
  
  // Se additionalInfo está dentro de support, mover para o nível raiz
  if (transformed.support && transformed.support.additionalInfo && Array.isArray(transformed.support.additionalInfo)) {
    // Mover additionalInfo para o nível raiz
    transformed.additionalInfo = [...transformed.support.additionalInfo];
    // Remover additionalInfo de dentro de support
    delete transformed.support.additionalInfo;
  }
  
  // Garantir que additionalInfo existe no nível raiz (pode não existir em agentes muito antigos)
  if (!transformed.additionalInfo) {
    transformed.additionalInfo = [];
  }
  
  return transformed;
};

// Iniciar processo de duplicação de agente
const initiateDuplicateAgent = (agent, event) => {
  if (event) {
    event.stopPropagation();
  }
  agentToDuplicate.value = agent;
  showDuplicateConfirmation.value = true;
};

// Confirmar duplicação do agente
const confirmDuplicateAgent = async () => {
  if (!agentToDuplicate.value) return;
  
  const agent = agentToDuplicate.value;
  
  // Fechar o modal de confirmação
  showDuplicateConfirmation.value = false;
  
  // Executar a duplicação
  await duplicateAgent(agent);
  
  // Limpar referência
  agentToDuplicate.value = null;
};

// Duplicar um agente
const duplicateAgent = async (agent) => {
  try {
    // console.log('Duplicando agente:', agent.name);
    
    // Transformar company_data para o novo formato (a função já faz o parse se necessário)
    const companyData = transformCompanyData(agent.company_data);
    
    // Parse customer_data se for string
    let userFields = agent.customer_data || [];
    if (typeof userFields === 'string') {
      try {
        userFields = JSON.parse(userFields);
      } catch (e) {
        userFields = [];
      }
    }
    
    // Criar os dados no formato esperado pelo controller
    const duplicatedAgentData = {
      name: agent.name,
      voice: agent.voice,
      personalityId: String(agent.personality),
      conversationFlowId: String(agent.flow),
      company: companyData,
      userFields: userFields
    };
    
    // Obter headers de autenticação
    const authHeaders = getAuthHeaders();
    
    // Criar o novo agente via API
    const response = await fetch('/api/v1/agents_ai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      credentials: 'include',
      body: JSON.stringify({ agent: duplicatedAgentData })
    });

    if (!response.ok) {
      throw new Error(`Erro ao duplicar agente: ${response.status}`);
    }

    const data = await response.json();
    
    // Verificar se o agente foi criado com sucesso
    if (!data || !data.success) {
      throw new Error('Falha ao criar agente');
    }
    
    // Recarregar a lista de agentes para obter o novo agente
    await fetchAgents();
    
    // Encontrar o agente recém-criado pelo ID
    const newAgentId = data.id;
    const duplicatedAgent = localAgents.value.find(a => a.id === newAgentId);
    
    if (duplicatedAgent) {
      // Navegar para a página de detalhes do agente duplicado
      router.push({
        name: 'agentis_view',
        params: { 
          accountId: route.params.accountId,
          agentId: duplicatedAgent.id 
        }
      });
    } else {
    }
  } catch (error) {
  }
};

// Configuração da tabela usando TanStack Table
const columnHelper = createColumnHelper();

const columns = [
  columnHelper.accessor('name', {
    header: () => 'Nome',
     cell: info => h(TruncatedCell, {
       content: info.getValue(),
       maxLength: 40,
       maxLines: 2
     }),
    enableSorting: true,
    minSize: 150,
  }),
  columnHelper.accessor('voice', {
    header: () => 'Tipo de Voz',
    cell: info => voiceOptions[info.getValue()] || info.getValue() || '-',
    enableSorting: true,
    minSize: 100,
  }),
  columnHelper.accessor(row => getCompanyRole(row), {
    id: 'role',
    header: () => 'Função',
    cell: info => info.getValue() || '-',
    enableSorting: true,
    minSize: 150,
  }),
  columnHelper.accessor('flow', {
    header: () => 'Fluxo',
    cell: info => flowTypes[info.getValue()] || '-',
    enableSorting: true,
    minSize: 120,
  }),
  columnHelper.accessor('personality', {
    header: () => 'Personalidade',
    cell: info => personalityTypes[info.getValue()] || '-',
    enableSorting: true,
    minSize: 120,
  }),
  columnHelper.accessor(row => row.id, {
    id: 'actions',
    header: () => 'Ações',
    cell: info => {
      const agent = info.row.original;
      return h('div', { class: 'flex items-center space-x-2' }, [
        h(Button, {
          variant: 'ghost',
          color: 'slate',
          size: 'sm',
          icon: 'i-lucide-settings',
          onClick: event => {
            event.stopPropagation();
            openAgentSettingsModal(agent);
          }
        }),
        h(Button, {
          variant: 'ghost',
          color: 'blue',
          size: 'sm',
          icon: 'i-lucide-copy',
          onClick: event => {
            event.stopPropagation();
            initiateDuplicateAgent(agent, event);
          }
        }),
        h(Button, {
          variant: 'ghost',
          color: 'ruby',
          size: 'sm',
          icon: 'i-lucide-trash-2',
          onClick: event => {
            event.stopPropagation();
            initiateDeleteAgent(agent, event);
          }
        })
      ]);
    },
    enableSorting: false,
    minSize: 100,
    maxSize: 120,
  }),
];

// Configuração da paginação e manipulação de dados
const pagination = reactive({
  pageIndex: 0,
  pageSize: 10,
});

// Usamos computed para filtrar os agentes com base na paginação
const paginatedAgents = computed(() => {
  const start = pagination.pageIndex * pagination.pageSize;
  const end = start + pagination.pageSize;
  return agents.value.slice(start, end);
});

// Criar a instância da tabela apenas uma vez
const table = useVueTable({
  get data() {
    return paginatedAgents.value;
  },
  columns,
  state: {
    get sorting() {
      return sorting.value;
    },
    get pagination() {
      return pagination;
    }
  },
  onSortingChange: updater => {
    sorting.value = typeof updater === 'function' ? updater(sorting.value) : updater;
  },
  onPaginationChange: updater => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
    Object.assign(pagination, newPagination);
  },
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  manualPagination: true,
  get pageCount() {
    return Math.ceil(agents.value.length / pagination.pageSize);
  },
  getRowId: row => row?.id || `temp-${Math.random()}`,
});

// Manipulador de clique na linha
const handleRowClick = (row) => {
  viewAgent(row.original);
};

// Função para abrir modal de configurações do agente
const openAgentSettingsModal = async (agent) => {
  selectedAgentForSettings.value = agent;
  showAgentSettingsModal.value = true;
  
  // Carregar inboxes disponíveis
  await loadInboxesForModal();
  
  // Carregar inboxes do agente
  await loadAgentInboxesForModal();
};

// Carregar inboxes disponíveis para o modal
const loadInboxesForModal = async () => {
  try {
    loadingAgentInboxes.value = true;
    
    // Get data from Vuex store (already loaded by App.vue)
    const inboxMetadata = store.getters['inboxMetadata/getFormattedInboxes'];
    
    // Filtrar apenas inboxes conectados
    const connectedInboxes = (inboxMetadata.results || []).filter(inbox => {
      return inbox.instances && inbox.instances.some(instance => 
        instance.connectionStatus === 2
      );
    });
    
    inboxOptionsForModal.value = connectedInboxes;
  } catch (err) {
    inboxOptionsForModal.value = [];
  } finally {
    loadingAgentInboxes.value = false;
  }
};

// Carregar inboxes do agente para o modal
const loadAgentInboxesForModal = async () => {
  if (!selectedAgentForSettings.value || inboxOptionsForModal.value.length === 0) return;
  
  try {
    const authHeaders = getAuthHeaders();
    const selectedInboxIds = [];
    
    // Para cada inbox, verificar se tem este agente
    for (const inbox of inboxOptionsForModal.value) {
      try {
        const response = await fetch(`/api/v1/inboxmeta/edit?inbox_id=${inbox.inbox_id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders
          },
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`Erro ao verificar inbox: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Verificar se o agente selecionado é este agente específico
        if (data.selected_agent === selectedAgentForSettings.value.name) {
          selectedInboxIds.push(String(inbox.inbox_id));
        }
      } catch (err) {
      }
    }
    
    // Atualizar os inboxes selecionados
    agentInboxes.value = selectedInboxIds;
  } catch (err) {
  }
};

// Função para navegar para edição do agente
const navigateToEditFromModal = () => {
  if (!selectedAgentForSettings.value) return;
  
  const agent = selectedAgentForSettings.value;
  
  // Criar uma cópia do agente para transformar
  const agentToEdit = JSON.parse(JSON.stringify(agent));
  
  // Transformar company_data para o novo formato
  if (agentToEdit.company_data) {
    agentToEdit.company_data = transformCompanyData(agentToEdit.company_data);
    
    // Definir valores padrão para áudio se não existirem
    if (agentToEdit.company_data.audioEnabled === undefined) {
      agentToEdit.company_data.audioEnabled = true;
    }
    if (agentToEdit.company_data.randomness === undefined) {
      agentToEdit.company_data.randomness = 40;
    }
    // Definir valores padrão para instruções customizadas de imagem
    if (agentToEdit.company_data.customImageInstructionsEnabled === undefined) {
      agentToEdit.company_data.customImageInstructionsEnabled = false;
    }
    if (agentToEdit.company_data.imagePrompt === undefined) {
      agentToEdit.company_data.imagePrompt = '';
    }
    
    // Incluir valores de áudio no nível raiz também
    agentToEdit.audioEnabled = agentToEdit.company_data.audioEnabled;
    agentToEdit.randomness = agentToEdit.company_data.randomness;
    // Incluir valores de instruções customizadas de imagem no nível raiz
    agentToEdit.customImageInstructionsEnabled = agentToEdit.company_data.customImageInstructionsEnabled;
    agentToEdit.imagePrompt = agentToEdit.company_data.imagePrompt;
  }
  
  // Processar customer_data (campos do usuário)
  let userFields = agent.customer_data || [];
  if (typeof userFields === 'string') {
    try {
      userFields = JSON.parse(userFields);
    } catch (e) {
      userFields = [];
    }
  }
  
  // Adicionar user_fields ao objeto editável
  agentToEdit.user_fields = Array.isArray(userFields) ? userFields : [];
  
  // Garantir que personality e flow sejam incluídos
  agentToEdit.personality = agent.personality || null;
  agentToEdit.flow = agent.flow || null;
  
  // Incluir additionalInfo e tagFields no nível raiz para compatibilidade
  agentToEdit.additionalInfo = Array.isArray(agentToEdit.company_data.additionalInfo) 
    ? agentToEdit.company_data.additionalInfo 
    : [];
  agentToEdit.tagFields = Array.isArray(agentToEdit.company_data.tagFields) 
    ? agentToEdit.company_data.tagFields 
    : [];
  
  // Armazenar dados do agente transformado no sessionStorage para edição
  sessionStorage.setItem('editingAgent', JSON.stringify(agentToEdit));
  
  // Fechar modal
  showAgentSettingsModal.value = false;
  
  // Navegar para a rota de criação com modo de edição
  router.push({
    name: 'agentis_create',
    query: { mode: 'edit', id: agent.id }
  });
};

// Função para navegar para configurações avançadas
const navigateToAdvancedSettingsFromModal = () => {
  if (!selectedAgentForSettings.value) return;
  
  const agent = selectedAgentForSettings.value;
  
  // Fazer uma cópia profunda dos dados da empresa
  let companyDataCopy = {};
  if (agent.company_data) {
    if (typeof agent.company_data === 'string') {
      try {
        companyDataCopy = JSON.parse(agent.company_data);
      } catch (e) {
        companyDataCopy = {};
      }
    } else {
      companyDataCopy = JSON.parse(JSON.stringify(agent.company_data));
    }
  }
  
  // Garantir que additionalInfo esteja no nível correto
  if (companyDataCopy.support && companyDataCopy.support.additionalInfo) {
    if (!companyDataCopy.additionalInfo) {
      companyDataCopy.additionalInfo = [];
    }
    companyDataCopy.additionalInfo = [...companyDataCopy.support.additionalInfo];
    delete companyDataCopy.support.additionalInfo;
  }
  
  // Definir valores padrão para áudio se não existirem
  if (companyDataCopy.audioEnabled === undefined) {
    companyDataCopy.audioEnabled = true;
  }
  if (companyDataCopy.randomness === undefined) {
    companyDataCopy.randomness = 40;
  }
  // Definir valores padrão para instruções customizadas de imagem
  if (companyDataCopy.customImageInstructionsEnabled === undefined) {
    companyDataCopy.customImageInstructionsEnabled = false;
  }
  if (companyDataCopy.imagePrompt === undefined) {
    companyDataCopy.imagePrompt = '';
  }
  
  // Processar customer_data
  let userFields = agent.customer_data || [];
  if (typeof userFields === 'string') {
    try {
      userFields = JSON.parse(userFields);
    } catch (e) {
      userFields = [];
    }
  }
  
  // Preparar os dados para edição avançada
  const advancedEditData = {
    id: agent.id,
    name: agent.name,
    voice: agent.voice,
    company_data: companyDataCopy,
    user_fields: Array.isArray(userFields) ? userFields : [],
    personality: agent.personality,
    flow: agent.flow,
    additionalInfo: Array.isArray(companyDataCopy.additionalInfo) ? companyDataCopy.additionalInfo : [],
    tagFields: Array.isArray(companyDataCopy.tagFields) ? companyDataCopy.tagFields : [],
    audioEnabled: companyDataCopy.audioEnabled,
    randomness: companyDataCopy.randomness,
    customImageInstructionsEnabled: companyDataCopy.customImageInstructionsEnabled,
    imagePrompt: companyDataCopy.imagePrompt
  };
  
  // Armazenar os dados no sessionStorage
  sessionStorage.setItem('advancedEditingAgent', JSON.stringify(advancedEditData));
  
  // Fechar modal
  showAgentSettingsModal.value = false;
  
  // Navegar para a página de configurações avançadas
  router.push({
    name: 'agentis_advanced_settings',
    params: { 
      accountId: route.params.accountId,
      agentId: agent.id 
    }
  });
};

// Helper function to get inbox display name
const getInboxDisplayName = (inbox) => {
  // First try to get display_name from store
  const displayName = store.getters['inboxMetadata/getInboxDisplayName'](inbox.inbox_id || inbox.id);
  if (displayName) return displayName;
  
  // Fallback to inbox_name or name
  return inbox.inbox_name || inbox.name || `Inbox ${inbox.inbox_id || inbox.id}`;
};

// Função para obter texto do dropdown de inboxes
const getSelectedInboxesText = () => {
  const selected = agentInboxes.value;
  
  if (selected.length === 0) {
    return 'Nenhum inbox selecionado';
  } else if (selected.length === 1) {
    const inbox = inboxOptionsForModal.value.find(inbox => 
      String(inbox.inbox_id) === selected[0]
    );
    return inbox ? getInboxDisplayName(inbox) : 'Inbox desconhecido';
  } else {
    return `${selected.length} inboxes selecionados`;
  }
};

// Função para verificar se inbox está selecionado
const isInboxSelectedModal = (inboxId) => {
  return agentInboxes.value.includes(String(inboxId));
};

// Função para alternar seleção de inbox
const toggleInboxSelectionModal = (inboxId) => {
  const currentSelection = [...agentInboxes.value];
  const inboxIdStr = String(inboxId);
  const inboxIndex = currentSelection.indexOf(inboxIdStr);
  
  if (inboxIndex === -1) {
    currentSelection.push(inboxIdStr);
  } else {
    currentSelection.splice(inboxIndex, 1);
  }
  
  agentInboxes.value = currentSelection;
};

// Função para alternar dropdown com posicionamento
const toggleInboxDropdownModal = (event) => {
  showInboxDropdownModal.value = !showInboxDropdownModal.value;
  
  if (showInboxDropdownModal.value) {
    // Calcular posição do dropdown
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const modalRect = button.closest('.relative').getBoundingClientRect();
    
    dropdownPositionModal.value = {
      top: rect.bottom - modalRect.top,
      left: rect.left - modalRect.left
    };
  }
};

// Função para salvar inboxes selecionados
const saveSelectedInboxesModal = async () => {
  if (!selectedAgentForSettings.value) return;
  
  try {
    const headers = getAuthHeaders();
    const agent = selectedAgentForSettings.value;
    
    // Primeiro, remover o agente de TODOS os inboxes
    const removePromises = inboxOptionsForModal.value.map(async (inbox) => {
      try {
        // Verificar se este inbox tem este agente
        const response = await wootAPI.get(
          `/api/v1/inboxmeta/edit`,
          {
            params: { inbox_id: inbox.inbox_id },
            headers
          }
        );
        
        // Se tem este agente, remover
        if (response.data.selected_agent === agent.name) {
          await wootAPI.post(
            `/api/v1/inboxmeta/update_meta`,
            {
              agent: '', // String vazia para remover
              inbox_id: parseInt(inbox.inbox_id),
            },
            { headers }
          );
        }
      } catch (err) {
      }
    });
    
    await Promise.all(removePromises);
    
    // Agora, adicionar o agente apenas aos inboxes selecionados.
    const savePromises = agentInboxes.value.map(async (inboxId) => {
      try {
        await wootAPI.post(
          `/api/v1/inboxmeta/update_meta`,
          {
            agent: agent.name,
            inbox_id: parseInt(inboxId),
          },
          { headers }
        );
        return { success: true, inboxId };
      } catch (err) {
        return { success: false, inboxId, error: err };
      }
    });
    
    const results = await Promise.all(savePromises);
    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;
    
    showInboxDropdownModal.value = false;
    
    if (successCount > 0 && failCount === 0) {
      useAlert(`Agente ${agent.name} atribuído a ${successCount} inbox${successCount > 1 ? 'es' : ''}!`);
    } else if (successCount > 0 && failCount > 0) {
      useAlert(`Agente atribuído a ${successCount} inbox${successCount > 1 ? 'es' : ''}. ${failCount} falhou.`, 'warning');
    } else if (agentInboxes.value.length === 0) {
      useAlert(`Agente ${agent.name} removido de todos os inboxes`);
    } else {
      useAlert('Erro ao atribuir agente aos inboxes', 'error');
    }
    
    // Emitir evento para recarregar nomes dos agentes
    emitter.emit('reload_agent_names');
  } catch (error) {
    useAlert('Erro ao salvar inboxes', 'error');
  }
};

// Função para obter classes do display de inboxes
const getSelectedInboxesDisplayClasses = () => {
  const selected = agentInboxes.value;
  
  if (selected.length === 0) {
    return 'bg-n-slate-2 text-n-slate-11';
  } else if (selected.length === 1) {
    const inbox = inboxOptionsForModal.value.find(inbox => 
      String(inbox.inbox_id) === selected[0]
    );
    if (!inbox) return 'bg-n-slate-2 text-n-slate-11';
    
    if (inbox.integration_type === 'official') {
      return 'bg-grass-300 text-grass-800 dark:bg-grass-200';
    } else if (inbox.integration_type === 'evolution') {
      return 'bg-n-amber-2 text-n-amber-11 dark:bg-n-amber-3 dark:text-n-amber-11';
    } else {
      return 'bg-n-slate-2 text-n-slate-11';
    }
  } else {
    return 'bg-n-amber-2 text-n-amber-11';
  }
};

// Função para obter ícone do display de inboxes
const getSelectedInboxesDisplayIcon = () => {
  const selected = agentInboxes.value;
  
  if (selected.length === 0) {
    return 'i-lucide-inbox';
  } else if (selected.length === 1) {
    const inbox = inboxOptionsForModal.value.find(inbox => 
      String(inbox.inbox_id) === selected[0]
    );
    return inbox ? getInboxTypeIcon(inbox.channel_type) : 'i-lucide-inbox';
  } else {
    return 'i-lucide-layers';
  }
};

// Função para detectar cliques fora do dropdown
const handleClickOutsideModal = (event) => {
  if (!event.target.closest('.inbox-dropdown-modal') && showInboxDropdownModal.value) {
    showInboxDropdownModal.value = false;
  }
};

// Função para obter ícone do tipo de inbox
const getInboxTypeIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'whatsapp':
      return 'i-lucide-message-circle';
    case 'facebook':
      return 'i-lucide-facebook';
    case 'twitter':
      return 'i-lucide-twitter';
    case 'email':
      return 'i-lucide-mail';
    case 'web':
      return 'i-lucide-globe';
    case 'telegram':
      return 'i-lucide-send';
    default:
      return 'i-lucide-message-square';
  }
};

// Computed para inboxes agrupados no modal
const groupedInboxOptionsModal = computed(() => {
  const result = [];
  const inboxes = inboxOptionsForModal.value || [];
  
  const whatsappEvolution = [];
  const whatsappOfficial = [];
  const otherChannels = [];
  
  inboxes.forEach(inbox => {
    const name = (inbox.inbox_name || inbox.name || '').toLowerCase();
    
    if (inbox.integration_type === 'official' || 
        name.includes('api oficial') ||
        name.includes('cloud') ||
        name.includes('official')) {
      whatsappOfficial.push({...inbox, integration_type: 'official'});
    }
    else if (inbox.integration_type === 'evolution' || name.includes('evolution') || name.includes('unofficial')) {
      whatsappEvolution.push({...inbox, integration_type: 'evolution'});
    }
    else {
      otherChannels.push(inbox);
    }
  });
  
  if (whatsappEvolution.length > 0) {
    result.push({ type: 'header', label: 'WHATSAPP NÃO OFICIAL' });
    result.push(...whatsappEvolution);
  }
  
  if (whatsappOfficial.length > 0) {
    result.push({ type: 'header', label: 'WHATSAPP API OFICIAL' });
    result.push(...whatsappOfficial);
  }
  
  if (otherChannels.length > 0) {
    result.push({ type: 'header', label: 'OUTROS CANAIS' });
    result.push(...otherChannels);
  }
  
  return result;
});

// Watch for modal open/close to handle body scroll
watch(showAgentSettingsModal, (isShowing) => {
  if (isShowing) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});

// Carregar agentes quando o componente for montado
onMounted(async () => {
  
  // Verificar conexão do Google Calendar primeiro
  await checkGoogleCalendarConnection();
  
  // Então carregar os agentes
  await fetchAgents();
  
  // Adicionar listener para cliques fora do dropdown do modal
  document.addEventListener('click', handleClickOutsideModal);
  

  nextTick(() => {
    checkScrollbar();
    
    // Observar mudanças no tamanho do conteúdo
    if (scrollContainer.value) {
      const resizeObserver = new ResizeObserver(() => {
        checkScrollbar();
      });
      resizeObserver.observe(scrollContainer.value);
      
      // Limpar observer ao desmontar
      onUnmounted(() => {
        resizeObserver.disconnect();
      });
    }
  });
});

// Limpar listeners ao desmontar
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutsideModal);
  // Restore body scroll if modal was open
  if (showAgentSettingsModal.value) {
    document.body.style.overflow = '';
  }
});

// Ref para o elemento com scroll
const scrollContainer = ref(null);

// Função para verificar e ajustar padding quando há scrollbar
const checkScrollbar = () => {
  if (scrollContainer.value) {
    // Força um reflow para garantir que as medidas estejam corretas
    scrollContainer.value.offsetHeight;
    
    const hasScrollbar = scrollContainer.value.scrollHeight > scrollContainer.value.clientHeight;
    if (hasScrollbar) {
      scrollContainer.value.style.paddingRight = '0px';
    } else {
      scrollContainer.value.style.paddingRight = '10px';
    }
  }
};

// Função para formatar data
const formatDate = (dateString) => {
  if (!dateString) return 'Não disponível';
  
  try {
    const date = new Date(dateString);
    const options = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return date.toLocaleDateString('pt-BR', options);
  } catch (error) {
    return 'Data inválida';
  }
};

</script>

<template>
  <div class="flex flex-col h-full bg-n-background w-full overflow-auto">
    <!-- Header -->
    <header
      class="flex justify-between items-center h-14 min-h-[3.5rem] px-4 py-2 bg-n-background border-b border-n-weak pl-6 flex-shrink-0">
      <div class="flex justify- items-center gap-2">
        <woot-sidemenu-icon v-if="showSidemenuIcon" />
        <h1 class="text-2xl font-semibold font-interDisplay tracking-[0.3px] text-slate-900 dark:text-slate-25">
          Agentes IA
        </h1>
      </div>
      <div v-if="agents && agents.length > 0" class="hidden md:block">
        <AddAgentButton />
      </div>
    </header>
    
    <!-- conteudo -->
    <div ref="scrollContainer" class="flex-1 overflow-y-auto custom-scrollbar padding-no-scroll">
      <p class="mb-0 text-sm font-normal text-n-slate-11 p-4 pb-0">
          Gerencie seus agentes de IA para atendimento ao cliente. Visualize, edite e configure seus assistentes virtuais.
      </p>

      <div v-if="isLoading || uiFlags.isFetching" class="flex items-center justify-center flex-grow p-10">
        <Spinner />
      </div>

      <div v-else-if="!agents || agents.length === 0" class="flex flex-col items-center justify-center flex-grow p-10 animate-fade-in">
        <div class="text-center">
          <span class="i-lucide-bot text-4xl text-n-slate-7 mb-4"></span>
          <h3 class="text-lg font-medium text-n-slate-12 mb-2">Nenhum agente encontrado</h3>
          <p class="text-n-slate-11 mb-6">Não existem agentes disponíveis para esta conta.</p>
          
          <!-- Botão de adicionar agente centralizado -->
          <AddAgentButton />

          <!-- Erro de autenticação -->
          <div v-if="uiFlags.error" class="mt-4 p-4 bg-red-50 text-red-600 rounded-lg">
            <h4 class="font-medium mb-2">Erro ao carregar agentes:</h4>
            <p>{{ uiFlags.error }}</p>
            <p v-if="uiFlags.error.includes('401')" class="mt-2">
              Você não está autenticado ou sua sessão expirou. Tente fazer login novamente.
            </p>
            <Button class="mt-4" color="blue" variant="solid" @click="fetchAgents">
              Tentar novamente
            </Button>
          </div>
        </div>
      </div>

      <div v-else class="flex-grow p-4 animate-fade-in">
        <div class="w-full bg-white dark:bg-slate-900 rounded-md border border-n-weak overflow-hidden">
          <!-- Tabela responsiva -->
          <div class="overflow-x-auto">
            <Table 
              :table="table" 
              :truncate-cells="true" 
              :max-cell-length="60" 
              :exclude-from-truncate="['actions', 'voice']"
              class="w-full" 
              @row-click="handleRowClick"
            />
          </div>
          <div class="p-4 border-t border-n-weak">
            <Pagination
              :table="table"
              :default-page-size="pagination.pageSize"
              :show-page-size-selector="true"
              :total="agents.length"
              @page-size-change="size => { pagination.pageSize = size; pagination.pageIndex = 0; }"
            />
          </div>
        </div>
      </div>


      <!-- Modal de confirmação de exclusão -->
      <AlertResponza
        :show="showDeleteConfirmation"
        type="delete"
        title="Excluir Agente"
        :message="agentToDelete ? `Tem certeza que deseja excluir o agente '${agentToDelete.name}'? Essa ação não pode ser desfeita.` : ''"
        confirm-text="Excluir agente"
        @confirm="confirmDeleteAgent"
        @cancel="showDeleteConfirmation = false"
        @close="showDeleteConfirmation = false"
      />

      <!-- Modal de confirmação de duplicação -->
      <AlertResponza
        :show="showDuplicateConfirmation"
        type="confirmation"
        title="Duplicar Agente"
        :message="agentToDuplicate ? `Deseja duplicar o agente '${agentToDuplicate.name}'? Uma cópia será criada com as mesmas configurações.` : ''"
        confirm-text="Duplicar agente"
        @confirm="confirmDuplicateAgent"
        @cancel="showDuplicateConfirmation = false"
        @close="showDuplicateConfirmation = false"
      />
      
      <!-- Botão flutuante de adicionar no mobile -->
      <div v-if="agents && agents.length > 0" class="md:hidden">
        <AddAgentButton />
      </div>
      
      <!-- Modal de Configurações do Agente -->
      <div v-if="showAgentSettingsModal" class="fixed inset-0 z-50 flex items-center justify-center bg-n-slate-12/50 dark:bg-n-slate-1/70 p-4">
        <!-- Backdrop -->
        <div class="absolute inset-0" @click="showAgentSettingsModal = false"></div>
        
        <!-- Modal Content -->
        <div class="relative bg-white dark:bg-slate-900 rounded-lg shadow-2xl max-w-md w-full max-h-[90vh]">
          <!-- Header -->
          <div class="flex items-center justify-between p-6 border-b border-n-weak">
            <h3 class="text-lg font-semibold text-n-slate-12">
              Configurações do Agente
            </h3>
            <button
              type="button"
              class="text-n-slate-10 hover:text-n-slate-12 transition-colors"
              @click="showAgentSettingsModal = false"
            >
              <i class="i-lucide-x w-5 h-5"></i>
            </button>
          </div>
          
          <!-- Body -->
          <div class="p-6 space-y-4">
            <!-- Informações de Data -->
            <div v-if="selectedAgentForSettings" class="bg-n-slate-1 p-4 rounded-lg border border-n-slate-3">
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <span class="text-sm text-n-slate-11">Criado em:</span>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ formatDate(selectedAgentForSettings.created_at) }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-n-slate-11">Última edição:</span>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ formatDate(selectedAgentForSettings.updated_at) }}
                  </span>
                </div>
              </div>
            </div>
            
            <!-- Botões de Ação -->
            <div class="space-y-3">
              <Button
                variant="solid"
                color="blue"
                size="md"
                icon="i-lucide-edit-3"
                class="w-full justify-start"
                @click="navigateToEditFromModal"
              >
                Editar Agente
              </Button>
              <!-- Pagina excluida por enquanto -->
              <!--
              <Button
                variant="outline"
                color="slate"
                size="md"
                icon="i-lucide-settings-2"
                class="w-full justify-start"
                @click="navigateToAdvancedSettingsFromModal"
              >
                Configurações Avançadas
              </Button>
              -->
            </div>
            
            <!-- Dropdown de Inboxes -->
            <div class="pt-4 border-t border-n-weak">
              <label class="block text-sm font-medium text-n-slate-11 mb-2">
                Inboxes Vinculados
              </label>
              <div class="relative inbox-dropdown-modal">
                <button
                  @click.stop="toggleInboxDropdownModal($event)"
                  class="flex items-center gap-2 px-3 py-2 border border-n-slate-4 rounded-lg text-sm bg-n-background text-n-slate-12 hover:bg-n-slate-1 focus:border-n-brand transition-colors w-full"
                  type="button"
                  :disabled="loadingAgentInboxes"
                >
                  <div 
                    class="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0"
                    :class="getSelectedInboxesDisplayClasses()"
                  >
                    <i :class="getSelectedInboxesDisplayIcon() + ' text-xs'"></i>
                  </div>
                  <span class="truncate flex-1 text-left">
                    {{ getSelectedInboxesText() }}
                  </span>
                  <i v-if="!loadingAgentInboxes" class="i-lucide-chevron-down ml-1 flex-shrink-0 transition-transform text-xs" :class="{ 'rotate-180': showInboxDropdownModal }"></i>
                  <i v-else class="i-lucide-loader animate-spin ml-1 flex-shrink-0 text-xs"></i>
                </button>
                
                <!-- Dropdown Options -->
                <div v-if="showInboxDropdownModal && !loadingAgentInboxes" 
                    class="absolute z-[9999] border border-n-slate-3 rounded-md shadow-lg bg-n-slate-2 overflow-hidden w-full mt-1"
                    :style="{
                      top: dropdownPositionModal.top + 'px',
                      left: dropdownPositionModal.left + 'px',
                      maxHeight: '400px'
                    }"
                    @click.stop>
                  <!-- Grouped Inboxes -->
                  <div v-if="inboxOptionsForModal.length === 0" class="p-4 text-center text-n-slate-10">
                    Nenhum inbox disponível
                  </div>
                  <div v-else class="max-h-32 overflow-y-auto">
                    <template v-for="(item, index) in groupedInboxOptionsModal" :key="index">
                      <!-- Group Header -->
                      <div v-if="item.type === 'header'"
                          class="px-3 py-1 text-xs font-semibold bg-n-slate-1 dark:bg-n-slate-3 text-n-slate-10 border-y border-n-slate-3 sticky top-0 z-10">
                        {{ item.label }}
                      </div>
                      
                      <!-- Inbox Option -->
                      <div v-else
                          class="p-2 hover:bg-n-slate-1 dark:hover:bg-n-slate-3 cursor-pointer"
                          @click.stop="toggleInboxSelectionModal(item.inbox_id)">
                        <div class="flex items-center">
                          <input type="checkbox" 
                                :checked="isInboxSelectedModal(item.inbox_id)" 
                                @click.stop
                                @change.stop="toggleInboxSelectionModal(item.inbox_id)"
                                class="mr-3">
                          <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3"
                              :class="{
                                'bg-n-amber-2 text-n-amber-11 dark:bg-n-amber-3 dark:text-n-amber-11': 
                                  item.integration_type === 'evolution',
                                'bg-grass-300 text-grass-800 dark:bg-grass-200': 
                                  item.integration_type === 'official',
                                'bg-n-slate-2 text-n-slate-11': 
                                  !item.integration_type
                              }">
                            <i :class="getInboxTypeIcon(item.channel_type)"></i>
                          </div>
                          
                          <div>
                            <div class="text-sm font-medium">
                              {{ getInboxDisplayName(item) }}
                            </div>
                            <div v-if="item.integration_type === 'official'" class="text-xs text-grass-800 dark:text-grass-800">
                              API Oficial
                            </div>
                            <div v-else-if="item.integration_type === 'evolution'" class="text-xs text-n-amber-11 dark:text-n-amber-11">
                              Não Oficial
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                  
                  <!-- Footer com botão de salvar -->
                  <div class="border-t border-n-slate-3 p-2 bg-n-slate-1 dark:bg-n-slate-3">
                    <Button
                      label="Salvar Seleção"
                      size="sm"
                      color="blue"
                      class="w-full"
                      @click="saveSelectedInboxesModal"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Status da Agenda -->
            <div v-if="selectedAgentForSettings && selectedAgentForSettings.flow === 3" class="pt-4 border-t border-n-weak">
              <label class="block text-sm font-medium text-n-slate-11 mb-2">
                Status da Agenda
              </label>
              <div class="flex items-center gap-2">
                <span v-if="isGoogleCalendarConnected" class="inline-flex items-center gap-2 px-3 py-1.5 bg-n-teal-2 text-n-teal-11 rounded-full text-sm">
                  <i class="i-lucide-calendar-check text-n-teal-9"></i>
                  Agenda vinculada
                </span>
                <span v-else class="inline-flex items-center gap-2 px-3 py-1.5 bg-n-amber-2 text-n-amber-11 rounded-full text-sm">
                  <i class="i-lucide-calendar-x text-n-amber-9"></i>
                  Agenda não vinculada
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

.animate-fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Barra de rolagem personalizada para o container principal */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  transition: background-color 0.3s ease;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.custom-scrollbar::-webkit-scrollbar-thumb:active {
  background-color: rgba(0, 0, 0, 0.4);
}

/* Tema escuro */
.dark .custom-scrollbar {
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:active {
  background-color: rgba(255, 255, 255, 0.4);
}

.padding-no-scroll {
  padding-left: 10px; /* Padding padrão */
  padding-right: 10px; /* Padding padrão */
}

</style>